import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { FloatLabel } from 'primeng/floatlabel';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { Divider } from 'primeng/divider';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize, take } from 'rxjs';
import { DialogAction } from '../../../../shared/contracts/dialog.interface';
import { UserService } from '../../../../users/services/user.service';
import { User } from '../../../../users/contracts/user.interface';
import { CompanyService } from '../../../services/company.service';
import { Company } from '../../../contracts/company.interface';
import { ContactService } from '../../../services/contact.service';
import { Contact } from '../../../contracts/contact.interface';
import { TeamService } from '../../../../teams/services/team.service';
import { Team } from '../../../../teams/contracts/team.interface';

export const PHONE_PREFIXES = [
  { label: '+39 Italia', value: '39' },
  { label: '+1 USA/Canada', value: '1' },
  { label: '+33 Francia', value: '33' },
  { label: '+34 Spagna', value: '34' },
  { label: '+41 Svizzera', value: '41' },
  { label: '+44 UK', value: '44' },
  { label: '+49 Germania', value: '49' },
  { label: '+351 Portogallo', value: '351' },
];

@Component({
  selector: 'kc-contact-dialog',
  standalone: true,
  imports: [
    CommonModule,
    Button,
    FormsModule,
    InputText,
    ReactiveFormsModule,
    FloatLabel,
    Select,
    MultiSelect,
    Divider,
  ],
  templateUrl: './contact-dialog.component.html',
  providers: [ContactService, CompanyService, UserService, TeamService],
})
export class ContactDialogComponent implements OnDestroy, OnInit {
  static readonly DIALOG_CONFIG: DynamicDialogConfig = {
    header: 'Dati Contatto',
    width: '95vw',
    style: { 'max-width': '700px' },
    modal: true,
    focusOnShow: false,
  };

  form!: FormGroup;
  users: User[] = [];
  companies: Company[] = [];
  teamOptions: (Team & { label: string })[] = [];
  phonePrefixes = PHONE_PREFIXES;
  phoneErrors: string[] = [];
  saving = false;
  isEditMode = false;
  contactId?: number;

  constructor(
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private userService: UserService,
    private companyService: CompanyService,
    private contactService: ContactService,
    private teamService: TeamService,
  ) {}

  get phoneNumbers(): FormArray {
    return this.form.get('phoneNumbers') as FormArray;
  }

  phoneGroup(i: number): FormGroup {
    return this.phoneNumbers.at(i) as FormGroup;
  }

  ngOnInit() {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [''],
      companyId: [null],
      userIds: [[]],
      teamIds: [[]],
      phoneNumbers: this.fb.array([]),
    });

    this.userService.getAll(1, 1000).pipe(take(1)).subscribe(data => (this.users = data.data));
    this.companyService.getAll().pipe(take(1)).subscribe(data => (this.companies = data.data));
    this.teamService.getAll(1, 1000).pipe(take(1)).subscribe(data => {
      this.teamOptions = data.data.map(t => ({ ...t, label: `${t.name} · ${t.membersCount ?? 0} membri` }));
    });

    if (this.config.data) {
      const contact: Contact = this.config.data;
      this.isEditMode = true;
      this.contactId = contact.id;

      this.form.patchValue({
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        email: contact.email ?? '',
        companyId: contact.companyId ?? null,
        userIds: contact.operators?.map(u => u.id) ?? [],
        teamIds: contact.teams?.map(t => t.id) ?? [],
      });

      (contact.phoneNumbers ?? []).forEach(pn => {
        let prefix = '39';
        let localNumber = pn.phoneNumber;
        for (const p of [...PHONE_PREFIXES].sort((a, b) => b.value.length - a.value.length)) {
          if (pn.phoneNumber.startsWith(p.value)) {
            prefix = p.value;
            localNumber = pn.phoneNumber.slice(p.value.length);
            break;
          }
        }
        this.phoneNumbers.push(
          this.fb.group({ prefix: [prefix], localNumber: [localNumber] }),
        );
        this.phoneErrors.push('');
      });
    }
  }

  addPhone() {
    this.phoneNumbers.push(this.fb.group({ prefix: ['39'], localNumber: [''] }));
    this.phoneErrors.push('');
  }

  removePhone(i: number) {
    this.phoneNumbers.removeAt(i);
    this.phoneErrors.splice(i, 1);
  }

  save() {
    if (this.form.invalid || this.saving) return;
    this.saving = true;
    this.phoneErrors = this.phoneErrors.map(() => '');

    const v = this.form.value;
    const payload = {
      firstName: v.firstName,
      lastName: v.lastName || undefined,
      email: v.email || undefined,
      companyId: v.companyId || undefined,
      operatorIds: v.userIds ?? [],
      teamIds: v.teamIds ?? [],
      phoneNumbers: v.phoneNumbers.map((pn: any) => pn.prefix + pn.localNumber),
    };

    const call$ = this.isEditMode
      ? this.contactService.update(this.contactId!, payload)
      : this.contactService.create(payload);

    call$
      .pipe(
        take(1),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: () => this.ref.close({ action: DialogAction.OK }),
        error: err => this.handleSaveError(err, v.phoneNumbers),
      });
  }

  private handleSaveError(err: any, formPhones: any[]) {
    if (err.status !== 409 && err.status !== 422) return;
    const body = err.error;
    const message: string = body?.detail ?? body?.message ?? body?.error ?? '';

    let matched = false;
    formPhones.forEach((pn: any, i: number) => {
      const full = pn.prefix + pn.localNumber;
      if (message.includes(full)) {
        this.phoneErrors[i] = 'Questo numero è già registrato';
        matched = true;
      }
    });

    if (!matched && /phone|numero|telefon|duplic/i.test(message)) {
      this.phoneErrors = this.phoneErrors.map(() => 'Questo numero è già registrato');
    }
  }

  closeDialog() {
    this.ref.close({ action: DialogAction.CLOSE });
  }

  ngOnDestroy() {
    if (this.ref) this.ref.close();
  }
}
